import { io, Socket } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private consumers = 0;
  private visibilityHandler: (() => void) | null = null;
  private pageShowHandler: ((e: PageTransitionEvent) => void) | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      // Reconnect when tab becomes visible again
      this.visibilityHandler = () => {
        if (
          document.visibilityState === 'visible' &&
          this.socket &&
          !this.socket.connected &&
          this.consumers > 0
        ) {
          this.reconnectAttempts = 0;
          this.socket.connect();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      // Reconnect after bfcache restore
      this.pageShowHandler = (e: PageTransitionEvent) => {
        if (
          e.persisted &&
          this.socket &&
          !this.socket.connected &&
          this.consumers > 0
        ) {
          this.reconnectAttempts = 0;
          this.socket.connect();
        }
      };
      window.addEventListener('pageshow', this.pageShowHandler);
    }
  }

  connect(userId: string, clinicId?: string): Socket {
    this.consumers++;

    // Reuse existing connected socket
    if (this.socket?.connected) {
      return this.socket;
    }

    // Clean up stale socket before creating new one
    if (this.socket) {
      this.forceDisconnect();
      this.consumers = 1;
    }

    this.reconnectAttempts = 0;

    this.socket = io(SOCKET_URL, {
      // WebSocket only — no long-polling fallback.
      // This keeps memory low: no XHR objects, no HTTP connection overhead.
      // The server takes the user and clinic from the access-token cookie.
      withCredentials: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      // Register user so the backend can route notifications to this socket
      this.socket?.emit('register', { userId, clinicId });
    });

    this.socket.on('disconnect', () => {
      this.isConnected = false;
      // Suppress reconnect when tab is hidden or no consumers remain
      if (document.visibilityState === 'hidden' || this.consumers === 0) {
        this.socket?.disconnect();
      }
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        // Give up — visibility handler will retry when the tab is focused again
        this.socket?.disconnect();
      }
    });

    return this.socket;
  }

  disconnect() {
    this.consumers = Math.max(0, this.consumers - 1);
    if (this.consumers === 0 && this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  forceDisconnect() {
    this.consumers = 0;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  joinClinic(clinicId: string) {
    if (this.socket?.connected) {
      this.socket.emit('join_clinic', clinicId);
    }
  }

  leaveClinic(clinicId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leave_clinic', clinicId);
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isSocketConnected(): boolean {
    return this.isConnected && this.socket !== null;
  }
}

export const socketService = new SocketService();

if (import.meta.env.DEV && typeof window !== 'undefined') {
  // @ts-expect-error — expose for memory profiling in DevTools
  window.__SOCKET_SERVICE__ = socketService;
}
